import React, { useMemo } from "react";
import {
  MembershipRole,
  PermissionPickableWithDisabled,
  SystemRole,
} from "components/shared/DirectoryPicker/types";
import DirectoryPicker from "components/shared/DirectoryPicker";
import { includes } from "lodash";
import { GroupCategoryEntry } from "components/directory/api";

export function RoleDirectoryPicker(props: {
  query?: {
    include_membership_roles?: boolean;
    includeGroupCategories?: boolean;
    [param: string]: any;
  };
  [key: string]: any;
}) {
  const extraOptions = useMemo(
    () =>
      async function extraOptions(
        inputValue: string,
      ): Promise<PermissionPickableWithDisabled[]> {
        let options: PermissionPickableWithDisabled[] = [];

        options = options.concat(systemRoles(inputValue));
        if (props.query?.include_membership_roles)
          options = options.concat(networkMembershipRoles(inputValue));

        if (props.query?.includeGroupCategories) {
          options = options.concat(groupCategories(inputValue));
        }

        return options;
      },
    [
      props.query?.includeGroupCategories,
      props.query?.include_membership_roles,
    ],
  );

  return <DirectoryPicker {...props} multi extraOptions={extraOptions} />;
}

/**
 * UI element analogous to the selection of memberships in the Appointment-Create form (invitations) to assign the following users/roles to an action:
 *
 *  - Network administrators
 *  - Groups (all members)
 *  - Groups (administrators only)
 *  - Groups (membership role)
 *  - Network-wide memberships role
 *  - Individual memberships
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default React.forwardRef(function PermissionPicker(props, _ref) {
  return (
    <RoleDirectoryPicker
      {...props}
      placeholder={I18n.t("js.permission_picker.placeholder")}
      query={{ include_membership_roles: true }}
    />
  );
});

function systemRoles(inputValue: string): PermissionPickableWithDisabled[] {
  const systemRoles: SystemRole[] = [
    {
      type: "system_role",
      id: "network_admin",
      name: I18n.t("js.permission_picker.system_roles.network_admin"),
    },
    {
      type: "system_role",
      id: "network_member",
      name: I18n.t("js.permission_picker.system_roles.network_member"),
    },
  ];
  return filterByInputValue(systemRoles, inputValue);
}

export function networkMembershipRoles(
  inputValue: string,
): PermissionPickableWithDisabled[] {
  const membershipRoles: MembershipRole[] =
    Preload.current_network.membership_roles.map((r) => ({
      ...r,
      type: "membership_role",
    }));

  return filterByInputValue(membershipRoles, inputValue);
}

function groupCategories(inputValue: string): PermissionPickableWithDisabled[] {
  const groupCategories: GroupCategoryEntry[] =
    Preload.current_network.group_categories.map((gc) => ({
      ...gc,
      type: "group_category",
    }));

  return filterByInputValue(groupCategories, inputValue);
}

function filterByInputValue<T extends { name: string }>(
  items: T[],
  inputValue: string,
): T[] {
  return items.filter((r) =>
    includes(r.name.toLowerCase(), inputValue.toLowerCase()),
  );
}
